// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { OptionGroup } from "../OptionGroup"
import { ChipMultiSelect } from "../ChipMultiSelect"
import { Checkbox } from "../Checkbox"
import { Field } from "../Field"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { InfoButton } from "@/components/ui/InfoButton"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { centsToInputString, parseEurInputToCents } from "@/lib/budgetAmount"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
}

const RETAILERS = ["REWE", "EDEKA", "Lidl", "Aldi", "Kaufland", "Penny"]

/**
 * Step 11 (wire) — Budget Model v2 (ADR-052 §11.1, owner D-5): ONE optional
 * amount, the SOFT weekly spending goal, with "Skip for now"; no tiers, no
 * prefilled amount, no maximum checkout (that lives in Settings). Plus the
 * unchanged preferred retailers / delivery preference / offer-email consent.
 */
export function Step11Budget({ value, onChange }: Props) {
  const { t, locale } = useI18n()
  const weeklyGoalEurCents = (value.weeklyGoalEurCents as number | null | undefined) ?? null
  // Raw (possibly-undefined) value renders — an unanswered field must never
  // look like an explicit "None" was already chosen (item 30). "Committed"
  // is only used to reconstruct this field when a DIFFERENT control
  // changes.
  const preferredRetailersRaw = value.preferredRetailers as string[] | "none" | undefined
  const preferredRetailers = preferredRetailersRaw ?? "none"
  const deliveryPreference = (value.deliveryPreference as string | undefined) ?? null

  // The raw text the user typed (German decimal comma accepted); the cents
  // value in the draft is derived from it and never prefilled.
  const [goalText, setGoalText] = useState(() => centsToInputString(weeklyGoalEurCents, locale))
  const parsedGoal = parseEurInputToCents(goalText)
  const goalInvalid = parsedGoal.kind === "invalid"

  const patch = (fields: Partial<AnswerDraft>) =>
    onChange({
      weeklyGoalEurCents,
      preferredRetailers,
      deliveryPreference,
      ...fields,
    })

  function handleGoalChange(next: string) {
    setGoalText(next)
    const parsed = parseEurInputToCents(next)
    if (parsed.kind === "cents") patch({ weeklyGoalEurCents: parsed.cents })
    else if (parsed.kind === "empty") patch({ weeklyGoalEurCents: null })
    // "invalid": the draft keeps its last valid value; the field shows why.
  }

  function handleSkip() {
    setGoalText("")
    patch({ weeklyGoalEurCents: null })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Field
            label={t("views.budget.onboardingGoalLabel")}
            htmlFor="ob-weekly-goal"
            required={false}
            hint={t("views.budget.onboardingGoalHint")}
          >
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="text-sm text-ink-2">
                €
              </span>
              <Input
                id="ob-weekly-goal"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="w-32"
                value={goalText}
                aria-invalid={goalInvalid || undefined}
                aria-describedby={goalInvalid ? "ob-weekly-goal-error" : undefined}
                onChange={e => handleGoalChange(e.target.value)}
              />
              <span className="text-sm text-ink-2">{t("views.budget.perWeek")}</span>
              <InfoButton termId="weekly-spending-goal" />
            </div>
          </Field>
        </div>
        {goalInvalid && (
          <p id="ob-weekly-goal-error" role="alert" className="text-xs text-destructive">
            {t("views.budget.invalidAmount")}
          </p>
        )}
        <Button
          type="button"
          variant="ghost"
          className="min-h-11 w-fit"
          onClick={handleSkip}
        >
          {t("views.budget.onboardingSkip")}
        </Button>
      </div>
      <ChipMultiSelect
        legend={t("onboarding.step11.retailerLabel")}
        items={RETAILERS.map(r => ({ value: r, label: r }))}
        value={preferredRetailersRaw}
        onChange={next => patch({ preferredRetailers: next })}
        noneLabel={t("onboarding.step11.noPreference")}
      />
      <OptionGroup
        legend={t("onboarding.step11.deliveryLabel")}
        columns={3}
        value={deliveryPreference}
        onChange={next => patch({ deliveryPreference: next })}
        options={[
          { value: "pickup", label: t("onboarding.step11.pickup") },
          { value: "delivery", label: t("onboarding.step11.delivery") },
          { value: "either", label: t("onboarding.step11.either") },
          { value: "none", label: t("onboarding.step11.none") },
        ]}
      />

      <OfferEmailsOptIn />
    </div>
  )
}

/**
 * ADR-029 offer-email consent, surfaced at its highest-context moment
 * (owner/marketing decision 2026-08-28): the user has JUST named their
 * preferred stores — the exact instant "email me when those stores have
 * matching offers" is most relevant. Off by default and saved IMMEDIATELY
 * on toggle via its own PATCH (UWG §7 explicit consent — same semantics as
 * the Preferences-page toggle; consent state is always exactly what the
 * user tapped, never bundled into the step answer). Failure is non-blocking:
 * onboarding continues, the toggle just shows the error.
 */
function OfferEmailsOptIn() {
  const { t } = useI18n()
  const api = useApiClient()
  const [optedIn, setOptedIn] = useState(false)
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle")

  async function handleToggle(next: boolean) {
    setOptedIn(next)
    setSaveState("saving")
    try {
      await api.patchProfile({ offerEmailsOptIn: next })
      setSaveState("saved")
    } catch {
      setOptedIn(!next)
      setSaveState("error")
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-bb-xl border-2 border-lime/60 bg-lime/10 p-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">{t("onboarding.step11.offerEmailsTitle")}</p>
        <p className="text-sm text-ink-2">{t("onboarding.step11.offerEmailsBody")}</p>
      </div>
      <label
        htmlFor="ob-offer-emails"
        className={`flex cursor-pointer items-start gap-2 rounded-bb-md border p-3 text-sm transition-colors ${
          optedIn ? "border-green bg-green/10 text-ink" : "border-line bg-paper text-ink"
        }`}
      >
        <Checkbox
          id="ob-offer-emails"
          checked={optedIn}
          onCheckedChange={v => void handleToggle(v)}
        />
        <span className="font-medium">{t("onboarding.step11.offerEmailsToggle")}</span>
      </label>
      <p className="text-xs text-ink-2">{t("onboarding.step11.offerEmailsFinePrint")}</p>
      {saveState === "saved" && optedIn && (
        <p className="text-xs font-medium text-green">{t("onboarding.step11.offerEmailsSaved")}</p>
      )}
      {saveState === "error" && (
        <p className="text-xs text-destructive">{t("moreProfile.profile.offerEmailsError")}</p>
      )}
    </div>
  )
}
